import React, { act, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
  ListItem,
  List,
  ListSubheader,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { TiWeatherCloudy, TiWeatherPartlySunny } from "react-icons/ti";
import { FaLocationCrosshairs } from "react-icons/fa6";
import { FaWindowMaximize } from "react-icons/fa6";
import { TbAirConditioningDisabled } from "react-icons/tb";
import { TbAirConditioning } from "react-icons/tb";
import { PiSeatbeltBold } from "react-icons/pi";
import { FaCarOn } from "react-icons/fa6";
import { FaCar } from "react-icons/fa6";
import { BsLightbulb } from "react-icons/bs";
import { BsLightbulbFill } from "react-icons/bs";
import { FaRegWindowMinimize } from "react-icons/fa6";
import { IoMdLock } from "react-icons/io";
import { IoMdUnlock } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useToast } from "../../toast";
import { detailsTabsBoxStyle } from "../../../services/Utils";
import { MODULES, MODULE_TEXT } from "../../../services/Constants";
import useModule from "../../../hooks/useModule";
import {
  getVehicleShadow,
  updateRemoteFunctionShadow
} from "../../../services/vehicle";

const VehicleRemoteCommands = ({ vehicleId, vehicleData }) => {

  const [commands, setCommands] = React.useState({
    lampControl: {
      action: "OFF"
    },
    ignition: {
      action: "OFF"
    },
    climateControl: {
      action: "OFF"
    },
    locateMyVehicle: {
      action: "OFF"
    },
    airCondition: {
      action: "OFF"
    },
    windowControl: {
      FRONT_RIGHT: "UP",
      REAR_LEFT: "UP",
      FRONT_LEFT: "UP",
      REAR_RIGHT: "UP"
    },
    doorControl: {
      FRONT_RIGHT: "LOCK",
      FRONT_LEFT: "LOCK",
      REAR_LEFT: "LOCK",
      REAR_RIGHT: "LOCK"
    },
    seatBeltStatus: {
      FRONT_RIGHT: "FASTENED",
      FRONT_LEFT: "FASTENED",
      REAR_LEFT: "FASTENED",
      REAR_RIGHT: "FASTENED",
      REAR_MIDDLE: "FASTENED"
    }
  });

  const handleChange = (event) => {
    let signal = null;
    let data = null;
    let signalMapping = {
      lampControl: 'REMOTE_HEADLAMP_CONTROL',
      ignition: 'REMOTE_IGNITION',
      locateMyVehicle: 'LOCATE_MY_VEHICLE',
      climateControl: 'REMOTE_CLIMATE_CONTROL',
      airCondition: 'REMOTE_AIR_CONDITION_CONTROL',
      seatBeltStatus: 'REMOTE_SEATBELT_STATUS'
    };
    if (["lampControl", "ignition", "climateControl", "locateMyVehicle", "airCondition", "seatBeltStatus"].includes(event.target.name)) {
      signal = signalMapping[event.target.name];
      data = { action: event.target.checked ? "ON" : "OFF" };
      setCommands({
        ...commands,
        [event.target.name]: data
      });
    } else if (["frontRightWindow", "frontLeftWindow", "rearLeftWindow", "rearRightWindow"].includes(event.target.name)) {
      signal = 'REMOTE_WINDOW_CONTROL';
      const windowName = event.target.name
        .replace("Window", "")
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .toUpperCase();
      data = { door: windowName, action: event.target.checked ? "UP" : "DOWN" };
      setCommands({
        ...commands,
        windowControl: {
          ...commands.windowControl,
          [windowName]: event.target.checked ? "UP" : "DOWN"
        }
      });
    } else if (["frontRightDoor", "frontLeftDoor", "rearLeftDoor", "rearRightDoor"].includes(event.target.name)) {
      signal = 'REMOTE_DOOR_CONTROL';
      const doorName = event.target.name
        .replace("Door", "")
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .toUpperCase();
      data = { door: doorName, action: event.target.checked ? "LOCK" : "UNLOCK" };
      setCommands({
        ...commands,
        doorControl: {
          ...commands.doorControl,
          [doorName]: event.target.checked ? "LOCK" : "UNLOCK"
        }
      });
    } else if (["frontRightSeatBelt", "frontLeftSeatBelt", "rearLeftSeatBelt", "rearRightSeatBelt", "rearMiddleSeatBelt"].includes(event.target.name)) {
      signal = 'REMOTE_SEATBELT_STATUS';
      const seatBeltName = event.target.name
        .replace("SeatBelt", "")
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .toUpperCase();
      data = { seatBelt: seatBeltName, action: event.target.checked ? "FASTENED" : "UNFASTENED" };
      setCommands({
        ...commands,
        seatBeltStatus: {
          ...commands.seatBeltStatus,
          [seatBeltName]: event.target.checked ? "FASTENED" : "UNFASTENED"
        }
      });
    }

    mutate({
      vehicleKey: vehicleData?.device?.vehicleKey,
      serialNo: vehicleData?.device?.serialNo,
      signal,
      data
    });
  };


  // Access the client
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Queries
  const {
    data: { data: shadowData = {} } = {},
  } = useQuery(["getVehicleShadow", vehicleId], getVehicleShadow, {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Mutations
  const { mutate, isLoading: mutateLoading } = useMutation(
    updateRemoteFunctionShadow,
    {
      onSuccess: (data) => {
        if (data) {
          addToast({
            type: "success",
            message: `Remote Function updated.`,
            autoClose: 3000,
          });
        }
      },
      onError: (err) => {
        const {
          response: { data: errorData },
        } = err;
        if (err && errorData) {
          addToast({
            type: "error",
            message: errorData.message,
            autoClose: 3000,
          });
        }
      },
      onSettled: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries("getVehicleShadow");
      },
    }
  );

  useEffect(() => {
    if (shadowData && shadowData.payload) {
      const shadowPayload = JSON.parse(shadowData?.payload);
      const {
        state: {
          desired: {
            remoteFunctions = {}
          } = {} } = {},
      } = shadowPayload;
      console.log("Remote Commands from shadow data", remoteFunctions);
      setCommands(remoteFunctions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shadowData]);

  const { validate } = useModule();

  if (!validate(MODULES.REMOTE_COMMANDS)) {
    return <Box {...detailsTabsBoxStyle}>
      <Alert variant="outlined" severity="warning">
        {MODULE_TEXT.REMOTE_COMMANDS} module not enabled for {localStorage.selectedCustomer} customer
      </Alert>
    </Box>
  }

  if (!vehicleData?.device?.certAvailable) {
    return (
      <Box {...detailsTabsBoxStyle}>
        <Alert variant="outlined" severity="warning">
          Device setup incomplete!
        </Alert>
      </Box>
    );
  }

  return (
    <Box {...detailsTabsBoxStyle}>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
        <Grid size={{ xs: 2, sm: 6, md: 3, lg: 3 }}>
          <List
            sx={{ width: '90%', bgcolor: 'background.paper' }}
            subheader={<ListSubheader>Vehicle Settings</ListSubheader>}
          >
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.ignition?.action === "ON" ? <FaCarOn size={30} color="orange" /> : <FaCar size={30} />}
              </ListItemIcon>
              <ListItemText primary="Vehicle Ignition" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Off</Typography>
                <FormControlLabel
                  control={
                    <Switch edge="end" name="ignition" checked={commands?.ignition?.action === "ON"} onChange={handleChange} />
                  }
                />
                <Typography>On</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.climateControl?.action === "ON" ? <TiWeatherPartlySunny size={30} color="orange" /> : <TiWeatherCloudy size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Climate Control" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Off</Typography>
                <FormControlLabel
                  control={
                    <Switch name="climateControl" checked={commands?.climateControl?.action === "ON"} onChange={handleChange} />
                  }
                />
                <Typography>On</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.lampControl?.action === "ON" ? <BsLightbulbFill size={30} color="orange" /> : <BsLightbulb size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Vehicle Head lamp" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Off</Typography>
                <FormControlLabel
                  control={
                    <Switch name="lampControl" checked={commands?.lampControl?.action === "ON"} onChange={handleChange} />
                  }
                />
                <Typography>On</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.locateMyVehicle?.action === "ON" ? <FaLocationCrosshairs size={30} color="orange" /> : <FaLocationCrosshairs size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Locate my vehicle" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Off</Typography>
                <FormControlLabel
                  control={
                    <Switch name="locateMyVehicle" checked={commands?.locateMyVehicle?.action === "ON"} onChange={handleChange} />
                  }
                />
                <Typography>On</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.airCondition?.action === "ON" ? <TbAirConditioning size={30} color="orange" /> : <TbAirConditioningDisabled size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Air Condition" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Off</Typography>
                <FormControlLabel
                  control={
                    <Switch name="airCondition" checked={commands?.airCondition?.action === "ON"} onChange={handleChange} />
                  }
                />
                <Typography>On</Typography>
              </Stack>
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 2, sm: 6, md: 3, lg: 3 }}>
          <List
            sx={{ width: '90%', bgcolor: 'background.paper' }}
            subheader={<ListSubheader>Window Settings</ListSubheader>}
          >
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.windowControl?.['FRONT_LEFT'] === "UP" ? <FaWindowMaximize size={30} color="orange" /> : <FaRegWindowMinimize size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Left Window" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Down</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontLeftWindow" checked={commands?.windowControl?.['FRONT_LEFT'] === "UP"} onChange={handleChange} />
                  }
                />
                <Typography>Up</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.windowControl?.['FRONT_RIGHT'] === "UP" ? <FaWindowMaximize size={30} color="orange" /> : <FaRegWindowMinimize size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Right Window" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Down</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontRightWindow" checked={commands?.windowControl?.['FRONT_RIGHT'] === "UP"} onChange={handleChange} />
                  }
                />
                <Typography>Up</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.windowControl?.['REAR_LEFT'] === "UP" ? <FaWindowMaximize size={30} color="orange" /> : <FaRegWindowMinimize size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Left Window" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Down</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearLeftWindow" checked={commands?.windowControl?.['REAR_LEFT'] === "UP"} onChange={handleChange} />
                  }
                />
                <Typography>Up</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.windowControl?.['REAR_RIGHT'] === "UP" ? <FaWindowMaximize size={30} color="orange" /> : <FaRegWindowMinimize size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Right Window" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Down</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearRightWindow" checked={commands?.windowControl?.['REAR_RIGHT'] === "UP"} onChange={handleChange} />
                  }
                />
                <Typography>Up</Typography>
              </Stack>
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 2, sm: 6, md: 3, lg: 3 }}>
          <List
            sx={{ width: '90%', bgcolor: 'background.paper' }}
            subheader={<ListSubheader>Door Settings</ListSubheader>}
          >
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.doorControl?.['FRONT_LEFT'] === "LOCK" ? <IoMdLock size={30} color="orange" /> : <IoMdUnlock size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Left Door" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontLeftDoor" checked={commands?.doorControl?.['FRONT_LEFT'] === "LOCK"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.doorControl?.['FRONT_RIGHT'] === "LOCK" ? <IoMdLock size={30} color="orange" /> : <IoMdUnlock size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Right Door" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontRightDoor" checked={commands?.doorControl?.['FRONT_RIGHT'] === "LOCK"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.doorControl?.['REAR_LEFT'] === "LOCK" ? <IoMdLock size={30} color="orange" /> : <IoMdUnlock size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Left Door" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearLeftDoor" checked={commands?.doorControl?.['REAR_LEFT'] === "LOCK"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.doorControl?.['REAR_RIGHT'] === "LOCK" ? <IoMdLock size={30} color="orange" /> : <IoMdUnlock size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Right Door" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearRightDoor" checked={commands?.doorControl?.['REAR_RIGHT'] === "LOCK"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 2, sm: 6, md: 3, lg: 3 }}>
          <List
            sx={{ width: '90%', bgcolor: 'background.paper' }}
            subheader={<ListSubheader>Seat Belt Settings</ListSubheader>}
          >
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.seatBeltStatus?.['FRONT_LEFT'] === "FASTENED" ? <PiSeatbeltBold size={30} color="blue" /> : <PiSeatbeltBold size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Left Seat Belt" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontLeftSeatBelt" checked={commands?.seatBeltStatus?.['FRONT_LEFT'] === "FASTENED"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.seatBeltStatus?.['FRONT_RIGHT'] === "FASTENED" ? <PiSeatbeltBold size={30} color="blue" /> : <PiSeatbeltBold size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Front Right Seat Belt" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="frontRightSeatBelt" checked={commands?.seatBeltStatus?.['FRONT_RIGHT'] === "FASTENED"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.seatBeltStatus?.['REAR_LEFT'] === "FASTENED" ? <PiSeatbeltBold size={30} color="blue" /> : <PiSeatbeltBold size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Left Seat Belt" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearLeftSeatBelt" checked={commands?.seatBeltStatus?.['REAR_LEFT'] === "FASTENED"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.seatBeltStatus?.['REAR_RIGHT'] === "FASTENED" ? <PiSeatbeltBold size={30} color="blue" /> : <PiSeatbeltBold size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Right Seat Belt" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearRightSeatBelt" checked={commands?.seatBeltStatus?.['REAR_RIGHT'] === "FASTENED"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
            <ListItem sx={{ marginTop: '5%' }}>
              <ListItemIcon>
                {commands?.seatBeltStatus?.['REAR_MIDDLE'] === "FASTENED" ? <PiSeatbeltBold size={30} color="blue" /> : <PiSeatbeltBold size={30} color="grey" />}
              </ListItemIcon>
              <ListItemText primary="Rear Middle Seat Belt" />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography>Unlock</Typography>
                <FormControlLabel
                  control={
                    <Switch name="rearMiddleSeatBelt" checked={commands?.seatBeltStatus?.['REAR_MIDDLE'] === "FASTENED"} onChange={handleChange} />
                  }
                />
                <Typography>Lock</Typography>
              </Stack>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </Box >
  )
};

export default VehicleRemoteCommands;